import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Loader2,
  XCircle,
  Download,
  Clapperboard,
} from "lucide-react";
import {
  API,
  fmtDate,
  SCORE_STYLE,
  NOTE_CATEGORIES,
} from "./shared";

export function DashboardTab({ authHeader, onNavigate }) {
  const [data, setData] = useState(null);
  const [profileEmail, setProfileEmail] = useState(null);
  const [cmdFilter, setCmdFilter] = useState(null);
  const [adLoading, setAdLoading] = useState(false);
  const cmdRef = useRef(null);

  const downloadAd = async () => {
    setAdLoading(true);
    try {
      const res = await axios.get(`${API}/admin/media/tiktok-ad`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "soarlife_tiktok_fr.mp4";
      a.click();
      URL.revokeObjectURL(url);
    } catch {} finally {
      setAdLoading(false);
    }
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, { headers: authHeader() });
      setData(res.data);
    } catch {}
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return <div className="text-center py-16 text-[#899480]"><Loader2 className="animate-spin inline" size={22} /></div>;
  }
  const s = data.snapshot;
  const cards = [
    { label: "Active clients", value: s.active_clients, tone: "green", testid: "dash-active", filter: "all" },
    { label: "New registrations (7d)", value: s.new_registrations_7d, tone: "green", testid: "dash-newregs", filter: "new7d" },
    { label: "Upcoming consultations", value: s.upcoming_consultations, tone: "green", testid: "dash-upcoming", go: "appointments" },
    { label: "IVF clients in cycle", value: s.ivf_in_cycle, tone: "green", testid: "dash-ivf", filter: "ivf" },
    { label: "IUI clients in treatment", value: s.iui_in_treatment, tone: "green", testid: "dash-iui", filter: "iui" },
    { label: "Unread messages", value: s.unread_messages, tone: s.unread_messages > 0 ? "yellow" : "green", testid: "dash-unread", go: "chats" },
    { label: "New journal entries (7d)", value: s.new_journal_entries_7d, tone: "green", testid: "dash-journals", go: "reports" },
    { label: "Wellness alerts", value: s.wellness_alerts, tone: s.wellness_alerts > 0 ? "red" : "green", testid: "dash-wellness", filter: "wellness" },
    { label: "Goal assessments done", value: s.assessments_completed, tone: "green", testid: "dash-quiz", go: "overview" },
    { label: "Requiring follow-up", value: s.clients_requiring_followup, tone: s.clients_requiring_followup > 0 ? "red" : "green", testid: "dash-followup", filter: "followup" },
  ];
  const TONE = {
    green: "border-l-4 border-l-[#3F5043]",
    yellow: "border-l-4 border-l-[#D4AF37]",
    red: "border-l-4 border-l-[#875F45]",
  };
  const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString();
  const CMD_FILTERS = {
    all: { label: "Active clients", fn: (r) => !!r.active },
    new7d: { label: "Registered in the last 7 days", fn: (r) => (r.created_at || "") >= weekAgo },
    ivf: { label: "IVF clients in cycle", fn: (r) => r.pathway === "ivf" },
    iui: { label: "IUI clients in treatment", fn: (r) => r.pathway === "iui" },
    wellness: { label: "Wellness alerts — low mood reported", fn: (r) => r.mood != null && r.mood <= 2 },
    followup: { label: "Requiring follow-up", fn: (r) => r.alerts.some((a) => a.level === "red") },
  };
  const activeFilter = cmdFilter ? CMD_FILTERS[cmdFilter] : null;
  const visibleClients = activeFilter ? data.clients.filter(activeFilter.fn) : data.clients;

  const onCard = (c) => {
    if (c.filter) {
      setCmdFilter(c.filter);
      setTimeout(() => cmdRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    } else if (c.go && onNavigate) {
      onNavigate(c.go);
    }
  };

  return (
    <div data-testid="dashboard-tab" className="space-y-8">
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">Today's snapshot</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {cards.map((c) => (
            <button key={c.label} data-testid={c.testid} onClick={() => onCard(c)}
              title={`View ${c.label}`}
              className={`text-left bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-[#899480] focus:outline-none focus:ring-2 focus:ring-[#899480] ${TONE[c.tone]} ${
                cmdFilter === c.filter && c.filter ? "ring-2 ring-[#899480]" : ""
              }`}>
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#899480] font-semibold mb-1.5 leading-tight">{c.label}</div>
              <div className="font-serif text-2xl text-[#3F5043]">{c.value}</div>
            </button>
          ))}
        </div>
      </div>

      {data.alerts.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#875F45] font-semibold mb-3">At-risk alerts</div>
          <div className="space-y-2" data-testid="dash-alerts">
            {data.alerts.map((a, i) => (
              <button key={i} onClick={() => setProfileEmail(a.email)} data-testid={`dash-alert-${i}`}
                className="w-full text-left bg-[#875F45]/5 border border-[#875F45]/30 rounded-2xl px-4 py-3 flex items-center justify-between hover:bg-[#875F45]/10 transition-colors">
                <span className="text-sm text-[#3F5043]"><b>{a.client}</b> — {a.msg}</span>
                <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-full ${a.level === "red" ? "bg-[#875F45] text-white" : "bg-[#D4AF37] text-[#3F5043]"}`}>{a.level === "red" ? "Follow up" : "Review"}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div ref={cmdRef} className="scroll-mt-24">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#899480] font-semibold">Command center</div>
          {activeFilter && (
            <span
              data-testid="command-filter-chip"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] font-semibold bg-[#3F5043] text-[#F8F7F3] rounded-full px-3 py-1.5"
            >
              {activeFilter.label} · {visibleClients.length}
              <button
                onClick={() => setCmdFilter(null)}
                data-testid="command-filter-clear"
                aria-label="Clear filter"
                className="hover:text-[#D4AF37]"
              >
                <XCircle size={12} />
              </button>
            </span>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="bg-[#EDEDEE] text-left text-[10px] uppercase tracking-[0.15em] text-[#899480]">
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">Score</th>
                <th className="px-4 py-3">Mood</th>
                <th className="px-4 py-3">Next appt</th>
                <th className="px-4 py-3">Last contact</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody data-testid="command-center">
              {visibleClients.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#3F5043]/60">
                  {activeFilter ? `No clients match “${activeFilter.label}” right now.` : "No clients yet."}
                </td></tr>
              )}
              {visibleClients.map((r, i) => {
                const red = r.alerts.some((a) => a.level === "red");
                const yellow = !red && r.alerts.length > 0;
                return (
                  <tr key={r.email} data-testid={`command-row-${i}`} onClick={() => setProfileEmail(r.email)}
                    className="border-t border-[#BCB3A6]/20 cursor-pointer hover:bg-[#F8F7F3]">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#3F5043]">{r.full_name}</div>
                      <div className="text-[11px] text-[#899480]">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 text-[#3F5043]/80">{r.stage}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${SCORE_STYLE(r.score)}`}>{r.score}</span>
                    </td>
                    <td className="px-4 py-3 text-[#3F5043]/70">{r.mood ? `${r.mood}/5` : "—"}</td>
                    <td className="px-4 py-3 text-[#3F5043]/70">{r.next_appointment ? `${r.next_appointment.date}` : "—"}</td>
                    <td className="px-4 py-3 text-[#3F5043]/70">{r.last_contact ? fmtDate(r.last_contact) : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-1 rounded-full ${
                        red ? "bg-[#875F45] text-white" : yellow ? "bg-[#D4AF37]/60 text-[#3F5043]" : "bg-[#3F5043]/10 text-[#3F5043]"
                      }`}>
                        {red ? "Follow-up" : yellow ? "Review" : "On track"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {data.center && (
        <section className="mb-10 mt-10" data-testid="center-widgets">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">
            Consulting practice
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
            {[
              { label: "Revenue this month", value: `$${Number(data.center.revenue_this_month || 0).toFixed(2)}`, go: "invoices", testid: "center-revenue" },
              { label: "Outstanding invoices", value: `${data.center.outstanding_invoices_count} · $${Number(data.center.outstanding_invoices_amount || 0).toFixed(0)}`, go: "invoices", testid: "center-outstanding" },
              { label: "Pending follow-ups", value: data.center.pending_followups, go: "notes", testid: "center-followups" },
              { label: "Email activity (30d)", value: data.center.email_activity_30d, go: "emails", testid: "center-emails" },
              { label: "New clients (7d)", value: data.center.new_clients_7d, go: "clients", testid: "center-newclients" },
            ].map((c) => (
              <button key={c.testid} data-testid={c.testid} onClick={() => onNavigate && onNavigate(c.go)}
                className="text-left bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-[#899480] focus:outline-none focus:ring-2 focus:ring-[#899480] border-l-4 border-l-[#D4AF37]">
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#899480] font-semibold mb-1.5 leading-tight">{c.label}</div>
                <div className="font-serif text-2xl text-[#3F5043]">{c.value}</div>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4">
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#899480] font-semibold mb-2">Recent consulting sessions</div>
              {data.center.recent_sessions.length === 0 ? (
                <div className="text-xs text-[#899480]">No finalized sessions yet.</div>
              ) : data.center.recent_sessions.map((n) => (
                <div key={n.id} className="flex justify-between text-xs py-1.5 border-b border-[#EDEDEE] last:border-0" data-testid={`center-session-${n.id}`}>
                  <span className="text-[#3F5043]">{n.client_name}</span>
                  <span className="text-[#899480]">{fmtDate(n.finalized_at || n.created_at)}</span>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4">
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#899480] font-semibold mb-2">Recent client notes</div>
              {data.center.recent_notes.length === 0 ? (
                <div className="text-xs text-[#899480]">No notes yet.</div>
              ) : data.center.recent_notes.map((n) => (
                <div key={n.id} className="flex justify-between text-xs py-1.5 border-b border-[#EDEDEE] last:border-0" data-testid={`center-note-${n.id}`}>
                  <span className="text-[#3F5043]">{n.client_name}</span>
                  <span className="text-[#899480]">{n.status} · {fmtDate(n.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {profileEmail && (
        <ClientProfile email={profileEmail} authHeader={authHeader} onClose={() => { setProfileEmail(null); load(); }} />
      )}

      <section data-testid="marketing-assets">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">Marketing assets</div>
        <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#3F5043]/10 flex items-center justify-center">
              <Clapperboard size={18} className="text-[#3F5043]" />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#3F5043]">French TikTok Ad · 83s</div>
              <div className="text-xs text-[#899480]">AI spokesperson in scrubs, app walkthrough, French voiceover</div>
            </div>
          </div>
          <button onClick={downloadAd} disabled={adLoading} data-testid="tiktok-ad-download"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#875F45] transition-colors disabled:opacity-50">
            {adLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {adLoading ? "Preparing…" : "Download MP4"}
          </button>
        </div>
      </section>
    </div>
  );
}

export function ClientProfile({ email, authHeader, onClose }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState({ category: NOTE_CATEGORIES[0], content: "" });
  const [profile, setProfile] = useState({ diagnosis: "", stage: "", ttc_duration: "", stress_level: "", assigned_to: "", follow_up_date: "", story: "" });
  const [saved, setSaved] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients/${encodeURIComponent(email)}/profile`, { headers: authHeader() });
      setData(res.data);
      const p = res.data.client.profile || {};
      setProfile({
        diagnosis: p.diagnosis || "", stage: p.stage || "", ttc_duration: p.ttc_duration || "",
        stress_level: p.stress_level ?? "", assigned_to: p.assigned_to || "",
        follow_up_date: p.follow_up_date || "", story: p.story || "",
      });
    } catch {}
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const saveProfile = async () => {
    await axios.put(`${API}/admin/clients/${encodeURIComponent(email)}/profile`,
      { ...profile, stress_level: profile.stress_level === "" ? null : Number(profile.stress_level) },
      { headers: authHeader() });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    load();
  };

  const addNote = async () => {
    if (!note.content.trim()) return;
    await axios.post(`${API}/admin/clients/${encodeURIComponent(email)}/notes`, note, { headers: authHeader() });
    setNote({ ...note, content: "" });
    load();
  };

  const inputCls = "w-full bg-[#F8F7F3] border border-[#BCB3A6]/50 rounded-2xl px-3.5 py-2.5 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div className="fixed inset-0 z-50 bg-[#0D4A46]/60 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="client-profile">
      <div className="w-full max-w-2xl h-full bg-[#F8F7F3] overflow-y-auto p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        {!data ? (
          <div className="text-center py-16"><Loader2 className="animate-spin inline text-[#899480]" size={22} /></div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="font-serif text-3xl text-[#3F5043]">{data.client.full_name}</div>
                <div className="text-xs text-[#899480]">{data.client.email} · pathway: {data.roadmap.pathway || "not chosen"}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${SCORE_STYLE(data.score)}`} data-testid="profile-score">
                  Score {data.score}
                </span>
                <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#EDEDEE] flex items-center justify-center" data-testid="profile-close">
                  <XCircle size={16} className="text-[#3F5043]" />
                </button>
              </div>
            </div>

            {/* Journey stage + profile fields */}
            <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5 mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-3">Fertility journey status</div>
              <div className="flex flex-wrap gap-1.5 mb-4" data-testid="profile-stages">
                {data.stages.map((st) => (
                  <button key={st} onClick={() => setProfile({ ...profile, stage: st })} data-testid={`stage-${st.replace(/\W/g, "-")}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
                      profile.stage === st ? "bg-[#115E59] text-[#FFFFF8]" : "bg-[#EDEDEE] text-[#3F5043]/70 hover:bg-[#BCB3A6]/30"
                    }`}>
                    {st}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input placeholder="Diagnosis (e.g. PCOS, low AMH)" value={profile.diagnosis} onChange={(e) => setProfile({ ...profile, diagnosis: e.target.value })} className={inputCls} data-testid="profile-diagnosis" />
                <input placeholder="Trying to conceive for… (e.g. 2 years)" value={profile.ttc_duration} onChange={(e) => setProfile({ ...profile, ttc_duration: e.target.value })} className={inputCls} data-testid="profile-ttc" />
                <input placeholder="Stress level (1-10)" type="number" min="1" max="10" value={profile.stress_level} onChange={(e) => setProfile({ ...profile, stress_level: e.target.value })} className={inputCls} data-testid="profile-stress" />
                <input placeholder="Assigned team member" value={profile.assigned_to} onChange={(e) => setProfile({ ...profile, assigned_to: e.target.value })} className={inputCls} data-testid="profile-assigned" />
                <input type="date" value={profile.follow_up_date} onChange={(e) => setProfile({ ...profile, follow_up_date: e.target.value })} className={inputCls} data-testid="profile-followup" />
              </div>
              <textarea placeholder="Client story & journey — their situation, experiences, concerns, family-building goals…" rows={3}
                value={profile.story} onChange={(e) => setProfile({ ...profile, story: e.target.value })}
                className={`${inputCls} mt-3 resize-none`} data-testid="profile-story" />
              <button onClick={saveProfile} data-testid="profile-save"
                className="mt-3 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#875F45] transition-colors">
                {saved ? "Saved ✓" : "Save profile"}
              </button>
            </div>

            {/* Wellness + activity */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 text-center">
                <div className="font-serif text-2xl text-[#3F5043]">{data.roadmap.checkins_count}</div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-[#899480] font-semibold">Wellness check-ins</div>
              </div>
              <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 text-center">
                <div className="font-serif text-2xl text-[#3F5043]">{data.education_events.length}</div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-[#899480] font-semibold">Education views</div>
              </div>
              <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 text-center">
                <div className="font-serif text-2xl text-[#3F5043]">{data.journals.length}</div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-[#899480] font-semibold">Journal entries</div>
              </div>
            </div>

            {/* Notes center */}
            <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5 mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-3">Notes center</div>
              <div className="flex gap-2 mb-3">
                <select value={note.category} onChange={(e) => setNote({ ...note, category: e.target.value })}
                  className={inputCls} data-testid="note-category">
                  {NOTE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <textarea rows={2} placeholder="Add a note…" value={note.content}
                onChange={(e) => setNote({ ...note, content: e.target.value })}
                className={`${inputCls} resize-none mb-2`} data-testid="note-content" />
              <button onClick={addNote} data-testid="note-add"
                className="rounded-full px-5 py-2 bg-[#875F45] text-[#F8F7F3] text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#3F5043] transition-colors">
                Add note
              </button>
              <div className="mt-4 space-y-2" data-testid="notes-list">
                {data.notes.map((n, i) => (
                  <div key={n.id} data-testid={`note-${i}`} className="bg-[#F8F7F3] rounded-xl px-4 py-3">
                    <div className="flex justify-between text-[10px] text-[#899480] mb-1">
                      <span className="font-semibold uppercase tracking-[0.12em]">{n.category}</span>
                      <span>{fmtDate(n.created_at)} · {n.author}</span>
                    </div>
                    <div className="text-sm text-[#3F5043]/85">{n.content}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Meds + journals */}
            <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-3">Medications & journals</div>
              <div className="text-sm text-[#3F5043]/80 space-y-1 mb-3" data-testid="profile-meds">
                {data.medications.length === 0 && <span className="text-[#899480]">No active medication schedules.</span>}
                {data.medications.map((m) => (
                  <div key={m.id}>💊 {m.name}{m.dose ? ` · ${m.dose}` : ""} · {m.times.join(", ")}</div>
                ))}
              </div>
              {data.journals.slice(0, 3).map((j, i) => (
                <div key={j.id} className="text-xs text-[#3F5043]/70 bg-[#F8F7F3] rounded-xl px-3 py-2 mb-1.5" data-testid={`profile-journal-${i}`}>
                  <b>{j.tool}</b> · {fmtDate(j.created_at)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
